/**
 * Privacy.tsx — صفحة سياسة الخصوصية
 *
 * Saudi-PDPL compliant + WhatsApp Business compliant. Zero-JS, prerendered.
 * Lives at /privacy (exact URL is non-negotiable for WhatsApp automated checks).
 *
 * Structure: simplified legal header (logo + back-home), TOC, 15 sections,
 * highlighted WhatsApp section, full footer reuse.
 */
import { siteConfig } from "@/config/siteConfig";

const itqanLogo = "/brand/itqan-logo.png";

const TOC = [
  { id: "intro", label: "١. مقدمة" },
  { id: "who", label: "٢. من نحن" },
  { id: "data-collected", label: "٣. البيانات التي نجمعها" },
  { id: "data-use", label: "٤. كيف نستخدم بياناتك" },
  { id: "whatsapp", label: "٥. التواصل عبر واتساب وقنوات الرسائل" },
  { id: "cookies", label: "٦. ملفات تعريف الارتباط (الكوكيز)" },
  { id: "sharing", label: "٧. مشاركة البيانات" },
  { id: "retention", label: "٨. مدة الاحتفاظ بالبيانات" },
  { id: "rights", label: "٩. حقوقك بموجب نظام حماية البيانات الشخصية السعودي" },
  { id: "security", label: "١٠. أمان البيانات" },
  { id: "international", label: "١١. نقل البيانات خارج المملكة" },
  { id: "children", label: "١٢. خصوصية القاصرين" },
  { id: "updates", label: "١٣. تحديثات هذه السياسة" },
  { id: "contact", label: "١٤. التواصل معنا" },
  { id: "authority", label: "١٥. الجهة الرقابية" },
] as const;

const Privacy = () => (
  <>
    <a href="#main" className="skip-to-content">تخطّي إلى المحتوى</a>

    {/* ═════ Simplified Legal Header ═════ */}
    <header className="legal-header" role="banner">
      <div className="legal-header-inner">
        <a href="/" className="brand" aria-label="إتقان — الصفحة الرئيسية">
          <img
            src={itqanLogo}
            alt=""
            width={40}
            height={40}
            className="brand-logo"
            fetchPriority="high"
            decoding="async"
          />
        </a>
        <a href="/" className="back-home">
          <span aria-hidden="true">→</span>
          العودة للرئيسية
        </a>
      </div>
    </header>

    <main id="main" className="legal-main" role="main">
      <article className="legal-document">

        <header className="legal-doc-header">
          <span className="legal-eyebrow">سياسة قانونية · إتقان</span>
          <h1 className="legal-title">سياسة الخصوصية</h1>
          <p className="legal-meta">
            آخر تحديث: ٢٥ أبريل ٢٠٢٦ · تاريخ السريان: ٢٥ أبريل ٢٠٢٦ · الإصدار ١٫٠
          </p>
        </header>

        <nav className="legal-toc" aria-label="فهرس المحتويات">
          <h2 className="legal-toc-title">المحتويات</h2>
          <ol>
            {TOC.map((t) => (
              <li key={t.id}><a href={`#${t.id}`}>{t.label}</a></li>
            ))}
          </ol>
        </nav>

        <section id="intro" className="legal-section">
          <h2>١. مقدمة</h2>
          <p>
            تُوضّح هذه السياسة كيف تجمع منصة إتقان (المُشار إليها فيما بعد بـ"إتقان"، "نحن"، "ـنا") البيانات الشخصية وتستخدمها وتحميها، عند تفاعلك مع موقعنا الإلكتروني <strong>itqan.agency</strong>، أو تواصلك معنا عبر واتساب، البريد الإلكتروني، الهاتف، أو أي قناة أخرى.
          </p>
          <p>
            باستخدامك خدماتنا، فإنك توافق على ممارسات جمع البيانات ومعالجتها الموضّحة في هذه السياسة. إذا كنت لا توافق، يُرجى التوقّف عن استخدام موقعنا أو خدماتنا.
          </p>
          <p>
            نلتزم بأحكام <strong>نظام حماية البيانات الشخصية</strong> الصادر بالمرسوم الملكي رقم (م/١٩) في المملكة العربية السعودية، ولوائحه التنفيذية الصادرة عن الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا).
          </p>
        </section>

        <section id="who" className="legal-section">
          <h2>٢. من نحن</h2>
          <p>
            إتقان منصة سعودية متخصصة في بناء وإدارة المتاجر الإلكترونية والتسويق الرقمي. مقرّنا في الرياض، المملكة العربية السعودية. نقدّم خدماتنا للشركات والعلامات التجارية في السوق السعودي والخليجي.
          </p>
          <p>
            <strong>صفة المتحكم في البيانات:</strong> إتقان هي الجهة المتحكمة في بياناتك الشخصية وفق التعريف الوارد في نظام حماية البيانات الشخصية السعودي.
          </p>
        </section>

        <section id="data-collected" className="legal-section">
          <h2>٣. البيانات التي نجمعها</h2>
          <h3>أ. بيانات تقدّمها لنا مباشرة</h3>
          <ul>
            <li>الاسم الكامل واللقب</li>
            <li>البريد الإلكتروني</li>
            <li>رقم الجوال (مع الموافقة على التواصل عبر واتساب إذا اخترت ذلك)</li>
            <li>اسم الشركة أو العلامة التجارية</li>
            <li>المنصة الإلكترونية المستخدمة (سلة، زد، شوبيفاي، أخرى)</li>
            <li>تفاصيل المشروع، الميزانية المتوقعة، التوقيت المطلوب</li>
            <li>الخدمات التي تطلبها</li>
            <li>أي رسائل أو محتوى ترسله إلينا</li>
            <li>الوثائق والملفات التي تشاركها معنا أثناء التعاقد</li>
          </ul>
          <h3>ب. بيانات نجمعها تلقائيًا عند زيارة الموقع</h3>
          <ul>
            <li>عنوان IP للجهاز</li>
            <li>نوع المتصفح ونظام التشغيل</li>
            <li>صفحات الموقع التي زرتها وزمن البقاء على كل صفحة</li>
            <li>مصدر الإحالة (الموقع الذي أتيت منه)</li>
            <li>الكلمات المفتاحية التي استخدمتها للوصول إلينا (إن وُجدت)</li>
            <li>الإجراءات التي اتخذتها على الموقع (نقرات، نماذج، تنزيلات)</li>
          </ul>
          <h3>ج. بيانات تواصل عبر القنوات الرسمية</h3>
          <ul>
            <li>محادثات واتساب أعمال (راجع القسم ٥)</li>
            <li>رسائل البريد الإلكتروني</li>
            <li>تسجيلات المكالمات الهاتفية (نُعلمك مسبقًا قبل أي تسجيل)</li>
          </ul>
        </section>

        <section id="data-use" className="legal-section">
          <h2>٤. كيف نستخدم بياناتك</h2>
          <p>نستخدم بياناتك للأغراض التالية فقط، وبالحدّ الأدنى الضروري لتحقيقها:</p>
          <ul>
            <li><strong>الردّ على استفساراتك:</strong> الإجابة على أسئلتك، تقديم العروض، حجز الاستشارات.</li>
            <li><strong>تقديم خدماتنا:</strong> تنفيذ المشاريع المتعاقد عليها وفق الاتفاقية بيننا.</li>
            <li><strong>التواصل معك:</strong> عبر القنوات التي اخترتها (إيميل، جوال، واتساب).</li>
            <li><strong>تحسين خدماتنا:</strong> تحليل سلوك الزوار لتطوير المحتوى وتجربة الموقع.</li>
            <li><strong>إرسال محتوى تثقيفي:</strong> فقط إذا اشتركت طوعًا في النشرة البريدية.</li>
            <li><strong>الالتزام القانوني:</strong> حفظ السجلات المحاسبية، الردّ على الطلبات الرسمية من السلطات.</li>
            <li><strong>الأمان والاحتيال:</strong> الكشف عن محاولات الاحتيال أو الاستخدام غير المشروع.</li>
          </ul>
          <p>
            <strong>الأساس القانوني للمعالجة:</strong> الموافقة الصريحة منك (للنشرة البريدية)، تنفيذ العقد (للعملاء)، المصلحة المشروعة (تحسين الخدمات وحماية الموقع)، الالتزام القانوني (المتطلبات الضريبية والتنظيمية).
          </p>
        </section>

        <section id="whatsapp" className="legal-section legal-section-highlight">
          <h2>٥. التواصل عبر واتساب وقنوات الرسائل</h2>
          <p>
            نستخدم <strong>واتساب أعمال (WhatsApp Business)</strong> كقناة تواصل رسمية مع عملائنا الحاليين والمحتملين. عند اختيارك التواصل معنا عبر واتساب، فإنك توافق على ما يلي:
          </p>
          <ul>
            <li>تخضع رسائلك المُرسَلة عبر واتساب لشروط استخدام واتساب وسياسة خصوصيته، إضافةً لهذه السياسة.</li>
            <li>نستخدم رقم جوالك للردّ على استفساراتك المتعلّقة بخدماتنا فقط، ولا نستخدمه لأي غرض إعلاني خارج هذا النطاق.</li>
            <li>لا نُشارك رقم جوالك أو محتوى محادثاتك مع أي طرف ثالث، باستثناء مزوّدي خدمات معالجة الرسائل (مثل واتساب نفسها) الملتزمين بحماية البيانات.</li>
            <li>نحتفظ بسجلات المحادثات لمدة لا تتجاوز <strong>٢٤ شهرًا</strong> من تاريخ آخر تواصل، لأغراض جودة الخدمة والمتابعة، ثم تُحذف نهائيًا.</li>
            <li>يمكنك في أي وقت طلب حذف محادثاتك ورقمك من سجلاتنا عبر <a href="mailto:privacy@itqan.agency">privacy@itqan.agency</a>، وسنستجيب خلال ٣٠ يومًا كحدّ أقصى.</li>
            <li>لن نُرسل لك رسائل ترويجية عبر واتساب دون موافقتك الصريحة المسبقة.</li>
            <li>لإلغاء الاشتراك من رسائل واتساب، يكفي أن ترسل كلمة "إلغاء" أو "STOP" في المحادثة، وسيتمّ إيقاف التواصل خلال ٢٤ ساعة.</li>
          </ul>
          <p>
            نلتزم بالكامل بـ <strong>سياسة استخدام واتساب أعمال (WhatsApp Business Messaging Policy)</strong>، ولا نُرسل أي رسائل تسويقية مباشرة دون قاعدة موافقة صريحة (opt-in).
          </p>
        </section>

        <section id="cookies" className="legal-section">
          <h2>٦. ملفات تعريف الارتباط (الكوكيز)</h2>
          <p>نستخدم ملفات تعريف الارتباط لتحسين تجربتك على موقعنا. أنواع الكوكيز التي نستخدمها:</p>
          <ul>
            <li><strong>كوكيز ضرورية:</strong> أساسية لتشغيل الموقع، لا يمكن إيقافها (مثل تذكّر تفضيلاتك في الجلسة الحالية).</li>
            <li><strong>كوكيز تحليلية:</strong> تساعدنا على فهم كيفية استخدام الموقع (مثل Google Analytics)، نستخدمها بصورة مجمّعة دون تحديد هويتك.</li>
            <li><strong>كوكيز تسويقية:</strong> تستخدم لعرض إعلانات أكثر صلة (مثل Meta Pixel)، تُفعّل فقط بموافقتك.</li>
          </ul>
          <p>يمكنك التحكم في الكوكيز من إعدادات متصفّحك في أي وقت. تعطيل الكوكيز قد يؤثّر على بعض وظائف الموقع.</p>
        </section>

        <section id="sharing" className="legal-section">
          <h2>٧. مشاركة البيانات</h2>
          <p><strong>لا نبيع بياناتك الشخصية تحت أي ظرف.</strong> نُشاركها فقط في الحالات التالية:</p>
          <ul>
            <li><strong>مع مزوّدي الخدمات التقنية</strong> الذين يعالجون البيانات بالنيابة عنّا (استضافة، تحليلات، أنظمة CRM)، والذين يلتزمون تعاقديًا بحماية بياناتك بمستوى لا يقلّ عن مستوانا.</li>
            <li><strong>استجابةً لطلبات قانونية رسمية</strong> من السلطات السعودية، أو امتثالًا لأمر قضائي.</li>
            <li><strong>لحماية حقوقنا</strong> أو حقوق طرف ثالث، أو في حالات الاحتيال الموثّقة.</li>
            <li><strong>بموافقتك الصريحة</strong> في أي حالة أخرى لم تُذكر أعلاه.</li>
            <li><strong>في حالة الاندماج أو الاستحواذ:</strong> قد تُنقل بياناتك إلى الكيان الجديد، مع إخطارك مسبقًا.</li>
          </ul>
        </section>

        <section id="retention" className="legal-section">
          <h2>٨. مدة الاحتفاظ بالبيانات</h2>
          <p>نحتفظ ببياناتك للمدّة اللازمة لتحقيق الغرض الذي جُمعت من أجله:</p>
          <ul>
            <li><strong>بيانات تواصل العملاء المحتملين:</strong> ٢٤ شهرًا من تاريخ آخر تفاعل.</li>
            <li><strong>بيانات العملاء المتعاقدين:</strong> طوال مدّة العقد + ٧ سنوات بعده، التزامًا بالمتطلبات المحاسبية والضريبية.</li>
            <li><strong>محادثات واتساب:</strong> ٢٤ شهرًا من آخر تواصل.</li>
            <li><strong>بيانات النشرة البريدية:</strong> حتى إلغاء الاشتراك.</li>
            <li><strong>بيانات الموقع التحليلية:</strong> ٢٦ شهرًا (وفق إعدادات Google Analytics 4 الافتراضية).</li>
            <li><strong>الكوكيز:</strong> حسب نوع الكوكي (من جلسة واحدة إلى ١٢ شهرًا).</li>
          </ul>
          <p>بعد انتهاء مدد الاحتفاظ، تُحذف البيانات نهائيًا أو تُجهّل (anonymize) بصورة لا تسمح بالعودة لتحديد هويتك.</p>
        </section>

        <section id="rights" className="legal-section">
          <h2>٩. حقوقك بموجب نظام حماية البيانات الشخصية السعودي</h2>
          <p>وفقًا لأحكام النظام، تتمتع بالحقوق التالية:</p>
          <ul>
            <li><strong>الحقّ في العلم:</strong> معرفة الأساس النظامي لمعالجة بياناتك والغرض منها.</li>
            <li><strong>الحقّ في الوصول:</strong> طلب نسخة من بياناتك التي نحتفظ بها.</li>
            <li><strong>الحقّ في التصحيح:</strong> تصحيح أي بيانات غير دقيقة أو ناقصة.</li>
            <li><strong>الحقّ في الإتلاف:</strong> طلب حذف بياناتك (الحقّ في النسيان) متى انتفى الغرض من معالجتها.</li>
            <li><strong>الحقّ في الاعتراض:</strong> رفض معالجة بياناتك في حالات معيّنة.</li>
            <li><strong>الحقّ في النقل:</strong> الحصول على بياناتك بصيغة منظّمة قابلة للقراءة، لنقلها لجهة أخرى.</li>
            <li><strong>الحقّ في سحب الموافقة:</strong> سحب موافقتك في أيّ وقت دون التأثير على المعالجة السابقة.</li>
            <li><strong>الحقّ في الشكوى:</strong> تقديم شكوى للهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا).</li>
          </ul>
          <p>
            لممارسة أيّ من هذه الحقوق، تواصل معنا على <a href="mailto:privacy@itqan.agency">privacy@itqan.agency</a>. سنستجيب لطلبك خلال ٣٠ يومًا، وفي حالات معقّدة قد تمتدّ المدّة إلى ٦٠ يومًا مع إخطارك.
          </p>
        </section>

        <section id="security" className="legal-section">
          <h2>١٠. أمان البيانات</h2>
          <p>نطبّق إجراءات تقنية وتنظيمية مناسبة لحماية بياناتك من الوصول غير المُصرّح به أو التعديل أو الإتلاف:</p>
          <ul>
            <li>تشفير الاتصالات عبر بروتوكول HTTPS مع شهادة TLS صالحة.</li>
            <li>تشفير قواعد البيانات الحسّاسة في حال الاستراحة (encryption at rest).</li>
            <li>التحكم في الوصول بصلاحيات محدّدة لكلّ موظف وفق مبدأ "الحدّ الأدنى المطلوب".</li>
            <li>المراجعة الدورية للأنظمة والثغرات الأمنية.</li>
            <li>تدريب الفريق على ممارسات حماية البيانات.</li>
            <li>نسخ احتياطية مشفّرة بصورة دورية.</li>
            <li>اتفاقيات سرّية موقّعة مع جميع الموظفين والشركاء.</li>
          </ul>
          <p>
            في حال حدوث أي خرق أمني يؤثّر على بياناتك الشخصية، سنُبلغك خلال ٧٢ ساعة من اكتشاف الحادثة، ونُبلغ الهيئة السعودية للبيانات والذكاء الاصطناعي وفق المتطلبات النظامية.
          </p>
        </section>

        <section id="international" className="legal-section">
          <h2>١١. نقل البيانات خارج المملكة العربية السعودية</h2>
          <p>
            قد نستخدم مزوّدي خدمات يقع مقرّ خوادمهم خارج المملكة (مثل خدمات الاستضافة السحابية، أدوات التحليل، خدمات البريد الإلكتروني). في هذه الحالات، نضمن:
          </p>
          <ul>
            <li>وجود ضوابط حماية كافية وفقًا لمتطلبات نظام حماية البيانات السعودي.</li>
            <li>توقيع اتفاقيات معالجة بيانات (DPAs) مع جميع المزوّدين.</li>
            <li>التزام المزوّدين بمعايير دولية معتمدة (ISO 27001، SOC 2، GDPR).</li>
          </ul>
        </section>

        <section id="children" className="legal-section">
          <h2>١٢. خصوصية القاصرين</h2>
          <p>
            خدماتنا موجّهة للشركات وأصحاب الأعمال، وليست مخصّصة للأشخاص دون <strong>١٨ عامًا</strong>. لا نجمع عمدًا أي بيانات شخصية من القاصرين.
          </p>
          <p>
            إذا كنت أحد الوالدين أو ولي أمر واكتشفت أن قاصرًا قدّم لنا بياناته، يُرجى التواصل معنا فورًا على <a href="mailto:privacy@itqan.agency">privacy@itqan.agency</a> وسنقوم بحذف البيانات على الفور.
          </p>
        </section>

        <section id="updates" className="legal-section">
          <h2>١٣. تحديثات هذه السياسة</h2>
          <p>
            قد نُحدّث هذه السياسة من وقت لآخر لمواكبة التغييرات النظامية أو تطوير خدماتنا. سنبلّغك بأي تغييرات جوهرية عبر:
          </p>
          <ul>
            <li>إشعار بارز في الصفحة الرئيسية لفترة لا تقلّ عن ٣٠ يومًا.</li>
            <li>رسالة إلى بريدك الإلكتروني (إذا كنت من عملائنا الحاليين أو المشتركين في النشرة).</li>
          </ul>
          <p>
            سيُعرض دائمًا تاريخ آخر تحديث وتاريخ السريان في أعلى هذه الصفحة. استمرارك في استخدام خدماتنا بعد التحديث يُعدّ موافقة على السياسة المُحدّثة.
          </p>
        </section>

        <section id="contact" className="legal-section">
          <h2>١٤. التواصل معنا</h2>
          <p>لأي استفسار يخصّ هذه السياسة أو ممارساتنا في حماية البيانات:</p>
          <dl className="legal-contact-list">
            <dt>البريد المخصص للخصوصية:</dt>
            <dd><a href="mailto:privacy@itqan.agency">privacy@itqan.agency</a></dd>

            <dt>البريد العام:</dt>
            <dd><a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a></dd>

            <dt>الهاتف وواتساب:</dt>
            <dd dir="ltr"><a href={`tel:${siteConfig.phoneRaw}`}>{siteConfig.phone}</a></dd>

            <dt>العنوان البريدي:</dt>
            <dd>إتقان، حي العليا، الرياض، المملكة العربية السعودية</dd>

            <dt>ساعات الردّ:</dt>
            <dd>
              {siteConfig.workingDays.start} – {siteConfig.workingDays.end}، من{" "}
              {siteConfig.workingHours.start} إلى {siteConfig.workingHours.end} (بتوقيت الرياض)
            </dd>
          </dl>
        </section>

        <section id="authority" className="legal-section">
          <h2>١٥. الجهة الرقابية</h2>
          <p>
            الجهة المختصّة بالإشراف على تطبيق نظام حماية البيانات الشخصية في المملكة العربية السعودية:
          </p>
          <p>
            <strong>الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا)</strong>
            <br />
            الموقع الرسمي:{" "}
            <a href="https://sdaia.gov.sa" target="_blank" rel="noopener noreferrer">sdaia.gov.sa</a>
            <br />
            يمكنك تقديم شكوى مباشرة عبر قنواتها الرسمية إذا رأيت أنّ معالجتنا لبياناتك تخالف أحكام النظام.
          </p>
        </section>

        <footer className="legal-doc-footer">
          <p className="legal-version">
            <span>الإصدار ١٫٠</span>
            <span className="legal-divider">·</span>
            <span>تاريخ السريان: ٢٥ أبريل ٢٠٢٦</span>
            <span className="legal-divider">·</span>
            <span>آخر تحديث: ٢٥ أبريل ٢٠٢٦</span>
          </p>
          <a href="/" className="back-home-bottom">العودة إلى الصفحة الرئيسية ←</a>
        </footer>

      </article>
    </main>

    {/* ═════ Minimal Site Footer (consistent with brand, lighter than home) ═════ */}
    <footer role="contentinfo" className="legal-site-footer">
      <div className="legal-footer-inner">
        <p className="legal-footer-brand">© {new Date().getFullYear()} إتقان · جميع الحقوق محفوظة</p>
        <nav className="legal-footer-nav" aria-label="روابط قانونية">
          <a href="/">الرئيسية</a>
          <span aria-hidden="true">·</span>
          <a href="/privacy">سياسة الخصوصية</a>
          <span aria-hidden="true">·</span>
          <a href="/terms">الشروط والأحكام</a>
          <span aria-hidden="true">·</span>
          <a href="/#contact">تواصل</a>
        </nav>
      </div>
    </footer>
  </>
);

export default Privacy;
